// TravelNotesCarousel — handwritten "pocket-card" travel notes for each
// experience page, shown as a horizontal slider directly beneath the
// experience story copy.
//
// Each card is styled like a paper note pinned to a board:
//   • a strip of washi tape at the top
//   • a brass push-pin in the top-right
//   • soft drop shadow + slight forward tilt on the active card
//   • handwritten title + bulleted tips + signoff
//
// Built mobile-first: cards are sized to ~85 % of the viewport on mobile
// so the next card peeks in to invite swiping, and snap to fill on
// desktop. Dot pagination + prev/next chevrons mirror the room gallery
// pattern used elsewhere on the site.

import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Pin } from "lucide-react";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Paper-tone palette per card. Each tone defines paper, ink and accent
// colours so the cards feel distinct but stay within the Xaluca palette.
const TONES = {
  cream: {
    paper: "#FAF1E0",
    ink: "#3A2F26",
    accent: "#C16542",
    tape: "rgba(212,163,115,0.55)",
    pin: "#C16542",
  },
  sand: {
    paper: "#F2E5CC",
    ink: "#3A2F26",
    accent: "#A2724A",
    tape: "rgba(162,114,74,0.55)",
    pin: "#8E5A38",
  },
  blue: {
    paper: "#E5ECF1",
    ink: "#2A3A48",
    accent: "#5B7F95",
    tape: "rgba(91,127,149,0.55)",
    pin: "#3B5C73",
  },
};

const TravelNotesCarousel = ({ notes, lang = "en", testIdPrefix = "travel-notes" }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const onScroll = () => {
      // Determine which card centre is closest to the track viewport centre.
      const trackRect = track.getBoundingClientRect();
      const centre = trackRect.left + trackRect.width / 2;
      let best = 0;
      let bestDist = Infinity;
      Array.from(track.children).forEach((child, i) => {
        const r = child.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const d = Math.abs(c - centre);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setActive(best);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, [notes.length]);

  const goTo = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[idx];
    if (child) {
      child.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  };

  if (!notes || !notes.length) return null;

  const hasNav = notes.length > 1;
  const goPrev = () => goTo(Math.max(0, active - 1));
  const goNext = () => goTo(Math.min(notes.length - 1, active + 1));

  return (
    <div className="relative" data-testid={testIdPrefix}>
      {/* Sliding track */}
      <div
        ref={trackRef}
        className="flex gap-5 md:gap-7 overflow-x-auto snap-x snap-mandatory pb-6 pt-6 -mx-2 px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid={`${testIdPrefix}-track`}
        tabIndex={0}
        aria-roledescription="carousel"
      >
        {notes.map((note, i) => {
          const tone = TONES[note.tone] || TONES.cream;
          const isActive = i === active;
          return (
            <article
              key={`note-${i}`}
              className="snap-center shrink-0 relative basis-[82%] sm:basis-[60%] md:basis-[58%] lg:basis-[52%] transition-transform duration-500"
              style={{
                transform: isActive ? "rotate(-0.4deg)" : "rotate(0.8deg)",
              }}
              data-testid={`${testIdPrefix}-card-${i}`}
              aria-roledescription="slide"
              aria-label={pick(note.title, lang)}
            >
              <div
                className="relative px-7 md:px-9 pt-12 pb-9 shadow-[0_18px_36px_-18px_rgba(28,21,16,0.45),0_4px_10px_-6px_rgba(28,21,16,0.25)]"
                style={{ backgroundColor: tone.paper, color: tone.ink }}
              >
                {/* Washi tape strip at the top */}
                <span
                  aria-hidden="true"
                  className="absolute -top-2 left-1/2 -translate-x-1/2 w-28 h-6 rotate-[-2deg] rounded-[2px]"
                  style={{
                    backgroundColor: tone.tape,
                    backgroundImage:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 6px, transparent 6px 12px)",
                  }}
                />

                {/* Brass push-pin top-right */}
                <span
                  aria-hidden="true"
                  className="absolute top-3 right-4 w-7 h-7 rounded-full flex items-center justify-center shadow-[0_4px_8px_-2px_rgba(0,0,0,0.4)]"
                  style={{ backgroundColor: tone.pin }}
                >
                  <Pin className="w-3.5 h-3.5 text-[#FDFBF7]" strokeWidth={2} fill="currentColor" />
                </span>

                {/* Eyebrow */}
                <p
                  className="text-[11px] tracking-[0.24em] uppercase font-medium"
                  style={{ color: tone.accent }}
                >
                  {pick(note.eyebrow, lang)}
                </p>

                {/* Handwritten title */}
                <h4
                  className="font-handwritten mt-2 text-3xl md:text-4xl leading-[1.1]"
                  style={{ color: tone.ink }}
                >
                  {pick(note.title, lang)}
                </h4>
                <span
                  aria-hidden="true"
                  className="block mt-3 h-px w-12"
                  style={{ backgroundColor: tone.accent, opacity: 0.55 }}
                />

                {/* Bulleted tips */}
                <ul className="mt-5 space-y-4 font-handwritten text-[19px] md:text-[20px] leading-[1.45]">
                  {(note.bullets || []).map((b, j) => (
                    <li key={`bul-${j}`} className="flex gap-3">
                      <span
                        aria-hidden="true"
                        className="shrink-0 mt-[2px] font-bold"
                        style={{ color: tone.accent }}
                      >
                        ✓
                      </span>
                      <span>{pick(b, lang)}</span>
                    </li>
                  ))}
                </ul>

                {/* Signoff */}
                {note.signoff ? (
                  <p
                    className="mt-7 font-handwritten text-xl"
                    style={{ color: tone.accent }}
                  >
                    {pick(note.signoff, lang)}
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      {/* Prev / next + dot pagination */}
      {hasNav ? (
        <div className="flex items-center justify-center gap-4 mt-2">
          <button
            type="button"
            onClick={goPrev}
            disabled={active === 0}
            className="w-9 h-9 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:text-[#C16542] hover:border-[#C16542] disabled:opacity-30 disabled:hover:text-[#5C5248] disabled:hover:border-[#2C2621]/15 transition-colors rounded-full"
            aria-label="Previous note"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
          </button>
          <div className="flex items-center gap-2" role="tablist">
            {notes.map((_, i) => {
              const isOn = i === active;
              return (
                <button
                  type="button"
                  key={`dot-${i}`}
                  onClick={() => goTo(i)}
                  className={`transition-all duration-300 ${
                    isOn ? "bg-[#C16542] w-6 h-1.5" : "bg-[#2C2621]/25 hover:bg-[#2C2621]/40 w-1.5 h-1.5"
                  } rounded-full`}
                  aria-label={`Go to note ${i + 1}`}
                  aria-selected={isOn}
                  role="tab"
                  data-testid={`${testIdPrefix}-dot-${i}`}
                />
              );
            })}
          </div>
          <button
            type="button"
            onClick={goNext}
            disabled={active === notes.length - 1}
            className="w-9 h-9 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:text-[#C16542] hover:border-[#C16542] disabled:opacity-30 disabled:hover:text-[#5C5248] disabled:hover:border-[#2C2621]/15 transition-colors rounded-full"
            aria-label="Next note"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TravelNotesCarousel;
